import React, { useEffect, useRef } from "react";
import { Editor } from "@tiptap/react";
import { Plus, Trash } from "@phosphor-icons/react";

interface TableContextMenuProps {
  editor: Editor | null;
  position: { x: number; y: number } | null;
  onClose: () => void;
}

const TableContextMenu: React.FC<TableContextMenuProps> = ({
  editor,
  position,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!position || !menuRef.current) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    // Registered in an effect that only runs once `position` is set, i.e.
    // after the opening click/contextmenu has already finished its event
    // cycle — so there is no "immediate closure" to guard against.
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [position, onClose]);

  if (!editor || !position) {
    return null;
  }

  const isEditable = editor.isEditable;
  const isInTableCell =
    editor.isActive("tableCell") || editor.isActive("tableHeader");

  if (!isInTableCell) {
    return null;
  }

  // Calculate menu position to avoid going off-screen
  const getMenuPosition = () => {
    if (!position) return { x: 0, y: 0 };

    const menuWidth = 180; // min-w-[180px]
    const menuHeight = 280; // approximate height
    const padding = 10;

    let x = position.x;
    let y = position.y;

    // Adjust horizontal position if menu would go off-screen
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - padding;
    }
    if (x < padding) {
      x = padding;
    }

    // Adjust vertical position if menu would go off-screen
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - padding;
    }
    if (y < padding) {
      y = padding;
    }

    return { x, y };
  };

  const menuItems = [
    {
      label: "Insert row above",
      icon: <Plus size={16} />,
      onClick: () => {
        editor.chain().focus().addRowBefore().run();
        onClose();
      },
      disabled: !isEditable || !editor.can().addRowBefore(),
    },
    {
      label: "Insert row below",
      icon: <Plus size={16} />,
      onClick: () => {
        editor.chain().focus().addRowAfter().run();
        onClose();
      },
      disabled: !isEditable || !editor.can().addRowAfter(),
    },
    {
      label: "Insert column left",
      icon: <Plus size={16} />,
      onClick: () => {
        editor.chain().focus().addColumnBefore().run();
        onClose();
      },
      disabled: !isEditable || !editor.can().addColumnBefore(),
    },
    {
      label: "Insert column right",
      icon: <Plus size={16} />,
      onClick: () => {
        editor.chain().focus().addColumnAfter().run();
        onClose();
      },
      disabled: !isEditable || !editor.can().addColumnAfter(),
    },
    { type: "separator" },
    {
      label: "Delete row",
      icon: <Trash size={16} />,
      onClick: () => {
        editor.chain().focus().deleteRow().run();
        onClose();
      },
      disabled: !isEditable || !editor.can().deleteRow(),
    },
    {
      label: "Delete column",
      icon: <Trash size={16} />,
      onClick: () => {
        editor.chain().focus().deleteColumn().run();
        onClose();
      },
      disabled: !isEditable || !editor.can().deleteColumn(),
    },
    {
      label: "Delete table",
      icon: <Trash size={16} />,
      onClick: () => {
        editor.chain().focus().deleteTable().run();
        onClose();
      },
      disabled: !isEditable || !editor.can().deleteTable(),
    },
  ];

  const menuPosition = getMenuPosition();

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px]"
      style={{
        left: menuPosition.x,
        top: menuPosition.y,
        zIndex: 2147483647,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {menuItems.map((item, index) => {
        if (item.type === "separator") {
          return (
            <div
              key={`separator-${index}`}
              className="w-full h-px bg-gray-200 my-1"
            />
          );
        }

        return (
          <button
            key={item.label}
            onClick={item.onClick}
            disabled={item.disabled}
            className={`
              flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 
              transition-colors text-left
              ${
                item.disabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100 cursor-pointer"
              }
            `}
          >
            <span className="flex-shrink-0">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default TableContextMenu;
